import { useCallback, useEffect, useRef, useState } from "react";

const STORAGE_KEY = "hourglass_v2_timer";

type TimerPhase = "idle" | "running" | "paused" | "stopped";

interface TimerState {
	phase: TimerPhase;
	/** Seconds accumulated from prior start/pause cycles */
	accumulatedSeconds: number;
	/** Timestamp (ms) when timer was last started/resumed, or null if not running */
	startedAtMs: number | null;
	/** Final captured seconds when stopped */
	finalSeconds: number;
}

const IDLE_STATE: TimerState = {
	phase: "idle",
	accumulatedSeconds: 0,
	startedAtMs: null,
	finalSeconds: 0,
};

function isValidTimerState(v: unknown): v is TimerState {
	if (typeof v !== "object" || v === null) return false;
	const s = v as Record<string, unknown>;
	return (
		(s.phase === "idle" ||
			s.phase === "running" ||
			s.phase === "paused" ||
			s.phase === "stopped") &&
		typeof s.accumulatedSeconds === "number" &&
		s.accumulatedSeconds >= 0 &&
		(s.startedAtMs === null ||
			(typeof s.startedAtMs === "number" && s.startedAtMs > 0)) &&
		typeof s.finalSeconds === "number" &&
		s.finalSeconds >= 0
	);
}

function loadState(): TimerState {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return IDLE_STATE;
		const parsed: unknown = JSON.parse(raw);
		if (!isValidTimerState(parsed)) return IDLE_STATE;
		// If startedAtMs is in the future (clock skew), treat as paused
		if (parsed.startedAtMs !== null && parsed.startedAtMs > Date.now() + 1000) {
			return { ...parsed, startedAtMs: null, phase: "paused" };
		}
		return parsed;
	} catch {
		return IDLE_STATE;
	}
}

function saveState(state: TimerState) {
	try {
		if (state.phase === "idle") {
			localStorage.removeItem(STORAGE_KEY);
		} else {
			localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
		}
	} catch {
		/* ignore */
	}
}

function computeElapsed(state: TimerState): number {
	if (state.startedAtMs === null) return Math.floor(state.accumulatedSeconds);
	return Math.max(
		0,
		Math.floor(
			state.accumulatedSeconds + (Date.now() - state.startedAtMs) / 1000,
		),
	);
}

export interface UseTimerReturn {
	phase: TimerPhase;
	elapsedSeconds: number;
	finalSeconds: number;
	start: () => void;
	pause: () => void;
	resume: () => void;
	stop: () => void;
	finish: () => number | null;
	dismiss: () => void;
}

interface UseTimerOptions {
	/** Called when the timer is stopped, receives the final elapsed seconds */
	onStop?: (elapsedSeconds: number) => void;
}

export function useTimer({ onStop }: UseTimerOptions = {}): UseTimerReturn {
	const stateRef = useRef<TimerState>(IDLE_STATE);
	const initializedRef = useRef(false);
	if (!initializedRef.current) {
		stateRef.current = loadState();
		initializedRef.current = true;
	}

	const [phase, setPhase] = useState(() => stateRef.current.phase);
	const [elapsedSeconds, setElapsedSeconds] = useState(() =>
		computeElapsed(stateRef.current),
	);
	const [finalSeconds, setFinalSeconds] = useState(
		() => stateRef.current.finalSeconds,
	);
	const intervalRef = useRef<number | null>(null);

	const clearTick = useCallback(() => {
		if (intervalRef.current !== null) {
			window.clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
	}, []);

	const startTick = useCallback(() => {
		clearTick();
		intervalRef.current = window.setInterval(() => {
			setElapsedSeconds(computeElapsed(stateRef.current));
		}, 1000);
	}, [clearTick]);

	// Resume ticking if restored from localStorage in running state
	useEffect(() => {
		if (stateRef.current.phase === "running") startTick();
		return () => clearTick();
	}, [clearTick, startTick]);

	// Snap elapsed on tab refocus
	useEffect(() => {
		const onVisible = () => {
			if (
				document.visibilityState === "visible" &&
				stateRef.current.startedAtMs !== null
			) {
				setElapsedSeconds(computeElapsed(stateRef.current));
			}
		};
		document.addEventListener("visibilitychange", onVisible);
		return () => document.removeEventListener("visibilitychange", onVisible);
	}, []);

	const apply = useCallback((next: TimerState) => {
		stateRef.current = next;
		saveState(next);
		setPhase(next.phase);
		setElapsedSeconds(computeElapsed(next));
		setFinalSeconds(next.finalSeconds);
	}, []);

	const start = useCallback(() => {
		if (stateRef.current.phase !== "idle") return;
		const next: TimerState = {
			phase: "running",
			accumulatedSeconds: 0,
			startedAtMs: Date.now(),
			finalSeconds: 0,
		};
		apply(next);
		startTick();
	}, [apply, startTick]);

	const pause = useCallback(() => {
		if (stateRef.current.phase !== "running") return;
		clearTick();
		const elapsed = computeElapsed(stateRef.current);
		apply({
			phase: "paused",
			accumulatedSeconds: elapsed,
			startedAtMs: null,
			finalSeconds: 0,
		});
	}, [apply, clearTick]);

	const resume = useCallback(() => {
		const { phase } = stateRef.current;
		if (phase !== "paused" && phase !== "stopped") return;
		const seconds =
			phase === "stopped"
				? stateRef.current.finalSeconds
				: stateRef.current.accumulatedSeconds;
		const next: TimerState = {
			phase: "running",
			accumulatedSeconds: seconds,
			startedAtMs: Date.now(),
			finalSeconds: 0,
		};
		apply(next);
		startTick();
	}, [apply, startTick]);

	const stop = useCallback(() => {
		if (stateRef.current.phase === "idle") return;
		if (stateRef.current.phase === "stopped") {
			// Re-fire onStop for already-stopped timer (e.g. after page reload)
			onStop?.(stateRef.current.finalSeconds);
			return;
		}
		clearTick();
		const elapsed = computeElapsed(stateRef.current);
		apply({
			phase: "stopped",
			accumulatedSeconds: 0,
			startedAtMs: null,
			finalSeconds: elapsed,
		});
		onStop?.(elapsed);
	}, [apply, clearTick, onStop]);

	const finish = useCallback(() => {
		if (stateRef.current.phase === "idle") return null;

		clearTick();
		const elapsed =
			stateRef.current.phase === "stopped"
				? stateRef.current.finalSeconds
				: computeElapsed(stateRef.current);

		apply(IDLE_STATE);
		onStop?.(elapsed);
		return elapsed;
	}, [apply, clearTick, onStop]);

	const dismiss = useCallback(() => {
		clearTick();
		apply(IDLE_STATE);
	}, [apply, clearTick]);

	return {
		phase,
		elapsedSeconds,
		finalSeconds,
		start,
		pause,
		resume,
		stop,
		finish,
		dismiss,
	};
}
